export type ApiClientOptions = {
  baseUrl?: string;
  fetchImpl?: typeof fetch;
  timeoutMs?: number;
};

export class LunavoaApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code: string,
    readonly retryable: boolean,
    readonly requestId?: string,
  ) {
    super(message);
    this.name = "LunavoaApiError";
  }
}

function combineSignals(signal: AbortSignal | undefined, timeoutMs: number): { signal: AbortSignal; dispose: () => void } {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(new DOMException("Request timed out", "TimeoutError")), timeoutMs);
  const abort = () => controller.abort(signal?.reason);
  signal?.addEventListener("abort", abort, { once: true });
  return { signal: controller.signal, dispose: () => { clearTimeout(timer); signal?.removeEventListener("abort", abort); } };
}

export function createApiClient(options: ApiClientOptions = {}) {
  const baseUrl = (options.baseUrl ?? "/api").replace(/\/$/, "");
  const transport = options.fetchImpl ?? fetch;
  const defaultTimeout = options.timeoutMs ?? 8_000;

  async function request<T>(path: string, init: RequestInit = {}, timeoutMs = defaultTimeout): Promise<T> {
    const controlled = combineSignals(init.signal ?? undefined, timeoutMs);
    try {
      const response = await transport(`${baseUrl}${path.startsWith("/") ? path : `/${path}`}`, {
        ...init,
        signal: controlled.signal,
        credentials: "same-origin",
        headers: { accept: "application/json", ...(init.method === "POST" ? { "x-lunavoa-request": "1" } : {}), ...(init.body ? { "content-type": "application/json" } : {}), ...init.headers },
      });
      const body = await response.json().catch(() => ({})) as any;
      if (!response.ok) {
        const detail = body.error ?? {};
        throw new LunavoaApiError(detail.message ?? "LUNAVOA could not complete this request.", response.status, detail.code ?? `http_${response.status}`, Boolean(detail.retryable), detail.requestId);
      }
      return body as T;
    } catch (error) {
      if (error instanceof LunavoaApiError) throw error;
      if (controlled.signal.aborted) throw new LunavoaApiError("The request took too long. Please try again.", 408, "request_timeout", true);
      throw new LunavoaApiError("The LUNAVOA service is unavailable. Please try again.", 503, "network_unavailable", true);
    } finally {
      controlled.dispose();
    }
  }

  return {
    get: <T>(path: string, init: RequestInit = {}) => request<T>(path, { ...init, method: "GET" }),
    post: <T>(path: string, body?: unknown, init: RequestInit = {}) => request<T>(path, { ...init, method: "POST", body: body === undefined ? undefined : JSON.stringify(body) }),
  };
}

export const api = createApiClient();
